import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import config from '../config';
import { getFeedbackDetails } from '../util/feedbackUtils';
import { getFeedbackDetailedSummary, getFeedbackWithSummary } from '../util/summaryUtil';

const FEEDBACK_REQUESTS_PATH = '/proxy/feedback/feedback-requests';
const axiosInstance = axios.create({
	baseURL: config.feedbackApi.baseUrl,
	headers: {
		timeout: '60000',
	},
});

export const feedbackKeys = {
	allFeedbacks: ['feedbacks'],
	feedbackLists: () => [...feedbackKeys.allFeedbacks, 'feedbackList'],
	feedbackList: (params) => [...feedbackKeys.feedbackLists(), params],
	feedback: (params) => [...feedbackKeys.feedbackLists(), params],
	feedbackForms: ['feedbackForms'],
	people: (term) => ['people', term],
	interviewFeedback: (feedbackRequestId) => ['interviewFeedback', feedbackRequestId],
	recruitment: (id) => ['recruitment', id],
	summary: (submittalId) => ['summary', submittalId],
	jobDescription: (jobId, customerId, isATS) => ['jobDescription', jobId, customerId, isATS],
	resumeLink: (intervieweeId, customerId, isATS) => [
		'resumeLink',
		intervieweeId,
		customerId,
		isATS,
	],
};

export function useGetFeedbacksListQuery(params) {
	return useQuery({
		queryKey: feedbackKeys.feedbackList(params),
		queryFn: async () => {
			let response = null;
			response = await Promise.all([
				axiosInstance.get(FEEDBACK_REQUESTS_PATH, { params }),
				axiosInstance.get(`/proxy/feedback/feedbacks/submittal/${params.submittalId}/form`),
			]);
			return { feedback: response[0].data, summary: response[1].data };
		},
		select: (feedbacksList) => getFeedbackWithSummary(feedbacksList),
		retry: false,
	});
}

export function useGetFeedbackQuery(params) {
	return useQuery({
		queryKey: feedbackKeys.feedback(params),
		queryFn: async () => {
			const response = await axiosInstance.get(FEEDBACK_REQUESTS_PATH, { params });
			return response.data;
		},
		// eslint-disable-next-line no-magic-numbers
		staleTime: 30 * 1000,
		select: (feedbacks) => getFeedbackDetails(feedbacks, params.feedbackRequestId),
	});
}

export function useAssignFeedbackRequestMutation(submittalId) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data) => {
			const response = await axiosInstance.post(FEEDBACK_REQUESTS_PATH, data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: feedbackKeys.feedbackList({ submittalId }),
			});
		},
	});
}

export function useSearchPeopleMutation() {
	return useMutation({
		mutationFn: async (data) => {
			const response = await axiosInstance.post('/proxy/feedback/search-people', data);
			return response.data;
		},
		select: ({ count, total, nextPage, people }) => ({
			count,
			total,
			nextPage,
			people: people.slice().sort((a, b) => {
				if (a.name.givenName < b.name.givenName) {
					return -1;
				}

				if (a.name.givenName > b.name.givenName) {
					return 1;
				}

				return 0;
			}),
		}),
	});
}

export function useGetFeedbacksFormsQuery(enabled) {
	return useQuery({
		queryKey: feedbackKeys.feedbackForms,
		queryFn: async () => {
			const response = await axiosInstance.get('/proxy/feedback/forms');
			return response.data;
		},
		select: (forms) =>
			Object.values(
				forms.reduce((acc, obj) => {
					const trimmedName = Object.values(obj.displayNamesMap)[0].trim();
					if (!acc[trimmedName]) {
						// eslint-disable-next-line camelcase
						acc[trimmedName] = { ...obj, displayNamesMap: { en_US: trimmedName } };
					}
					return acc;
				}, {}),
			).sort((a, b) =>
				Object.values(a.displayNamesMap)[0].localeCompare(Object.values(b.displayNamesMap)[0]),
			),
		enabled,
	});
}

// API is not used anywhere
export function useGetRecruitmentQuery(id) {
	return useQuery({
		queryKey: feedbackKeys.recruitment(id),
		queryFn: async () => {
			const response = await axiosInstance.get(`/proxy/feedback/recruitments/${id}`);
			return response.data;
		},
	});
}

export function useGetFeedbackSummaryQuery(submittalId) {
	return useQuery({
		queryKey: feedbackKeys.summary(submittalId),
		queryFn: async () => {
			const response = await axiosInstance.get(
				`/proxy/feedback/feedbacks/submittal/${submittalId}/form`,
			);
			return response.data;
		},
		select: (summary) => getFeedbackDetailedSummary(summary),
	});
}

// POC for fetching job description and resume
export function useGetJobDescriptionQuery(jobId, customerId, isATS, enabled) {
	return useQuery({
		queryKey: feedbackKeys.jobDescription(jobId, customerId, isATS),
		queryFn: async () => {
			const response = await axiosInstance.get(
				`${isATS ? '/proxy/feedback' : ''}/job-description/${jobId}/customer/${customerId}`,
			);
			return response.data;
		},
		enabled,
		staleTime: Infinity,
	});
}

export function useGetResumeLinkQuery(intervieweeId, customerId, isATS, enabled) {
	return useQuery({
		queryKey: feedbackKeys.resumeLink(intervieweeId, customerId, isATS),
		queryFn: async () => {
			const response = await axiosInstance.get(
				`${isATS ? '/proxy/feedback' : ''}/resume-link/${intervieweeId}/customer/${customerId}`,
			);
			return response.data;
		},
		enabled,
		staleTime: Infinity,
	});
}

export function useResendFeedbackMutation(feedbackRequestId) {
	return useMutation({
		mutationFn: async () => {
			const response = await axiosInstance.post(
				`/proxy/feedback/feedback-requests/${feedbackRequestId}/reminder`,
			);
			return response.data;
		},
	});
}
